import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { 
  Activity, 
  Flame, 
  Footprints, 
  Heart, 
  Trophy, 
  TrendingUp,
  Target,
  Calendar,
  Dumbbell,
  ChevronRight,
  Zap,
  Award,
  Lock,
  Megaphone,
  MessageCircle,
  Pin,
  ClipboardList,
  AlertCircle,
  X,
  Clock,
  Sparkles,
  Quote,
  CheckCircle2,
  Share2,
  Copy,
  Check,
  Bell,
  BellOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { toast } from 'sonner';
import usePushNotifications from '../hooks/usePushNotifications';
import RecoveryInsightsWidget from '../components/dashboard/RecoveryInsightsWidget';

export default function DashboardPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [badges, setBadges] = useState({ earned: [], locked: [] });
  const [broadcasts, setBroadcasts] = useState([]);
  const [pendingForms, setPendingForms] = useState([]);
  const [dismissedReminder, setDismissedReminder] = useState(false);
  const [coachingMessage, setCoachingMessage] = useState(null);
  const [messageAcknowledged, setMessageAcknowledged] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Push notifications hook
  const { 
    isSupported: pushSupported, 
    isEnabled: pushEnabled, 
    isLoading: pushLoading,
    enableNotifications,
    sendTestNotification
  } = usePushNotifications();

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, badgesRes, broadcastsRes, formsRes, coachingRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/badges/my`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/broadcasts/feed?limit=3`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { broadcasts: [] } })),
        axios.get(`${API}/forms`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { forms: [] } })),
        axios.get(`${API}/coaching/message/today`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      setAnalytics(analyticsRes.data);
      setBadges(badgesRes.data);
      setBroadcasts(broadcastsRes.data.broadcasts || []);
      setCoachingMessage(coachingRes.data);
      
      // Filter forms that are required and not yet submitted by the user
      const forms = formsRes.data.forms || [];
      const pending = forms.filter(f => 
        f.is_required_for_onboarding && 
        !f.user_submission
      );
      setPendingForms(pending);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAcknowledgeMessage = async () => {
    if (!coachingMessage) return;
    try {
      await axios.post(
        `${API}/coaching/message/acknowledge?day_number=${coachingMessage.day_number}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessageAcknowledged(true);
    } catch (error) {
      console.error('Error acknowledging message:', error);
    }
  };

  // Share quote functions
  const getShareText = () => {
    if (!coachingMessage) return '';
    return `"${coachingMessage.message}"\n\n— Day ${coachingMessage.day_number} of my fitness journey with Bodies By Akeem 💪\n\n#BodiesByAkeem #FitnessMotivation #DailyCoaching`;
  };

  const getShareUrl = () => {
    return 'https://bodiesbyakeem.com';
  };

  const handleShareTwitter = () => {
    const text = encodeURIComponent(getShareText());
    const url = `https://twitter.com/intent/tweet?text=${text}`;
    window.open(url, '_blank', 'width=550,height=420');
    setShowShareMenu(false);
  };

  const handleShareFacebook = () => {
    const url = encodeURIComponent(getShareUrl());
    const quote = encodeURIComponent(`"${coachingMessage?.message}" — Day ${coachingMessage?.day_number} of my fitness journey with Bodies By Akeem`);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`, '_blank', 'width=550,height=420');
    setShowShareMenu(false);
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(getShareText());
    window.open(`https://wa.me/?text=${text}`, '_blank');
    setShowShareMenu(false);
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getShareText());
      setCopiedToClipboard(true);
      toast.success('Quote copied to clipboard!');
      setTimeout(() => {
        setCopiedToClipboard(false);
        setShowShareMenu(false);
      }, 1500);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Mock chart data
  const chartData = analytics?.recent_activity?.map((a, i) => ({
    day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i] || `Day ${i + 1}`,
    calories: a.calories_burned || Math.floor(Math.random() * 500 + 200),
    steps: a.steps || Math.floor(Math.random() * 8000 + 2000)
  })) || [
    { day: 'Mon', calories: 320, steps: 5200 },
    { day: 'Tue', calories: 450, steps: 7500 },
    { day: 'Wed', calories: 280, steps: 4000 },
    { day: 'Thu', calories: 520, steps: 9200 },
    { day: 'Fri', calories: 380, steps: 6100 },
    { day: 'Sat', calories: 600, steps: 11000 },
    { day: 'Sun', calories: 410, steps: 7800 }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const stats = analytics?.weekly_summary || { total_steps: 0, total_calories: 0, total_workouts: 0, active_minutes: 0 };
  const userStats = analytics?.user_stats || { total_points: 0, current_streak: 0, badges_count: 0 };
  const habits = analytics?.habits || { total: 0, completed_today: 0 };

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            Welcome Back, <span className="text-primary">{user?.first_name}</span>
          </h1>
          <p className="text-gray-400 mt-1">Let's crush your goals today!</p>
        </div>
        
        <Button 
          data-testid="start-workout-btn"
          onClick={() => navigate('/workouts')}
          className="bg-primary hover:bg-red-700 text-white uppercase font-semibold"
        >
          <Dumbbell className="w-4 h-4 mr-2" />
          Start Workout
        </Button>
      </div>

      {/* Form Completion Reminder Banner */}
      {pendingForms.length > 0 && !dismissedReminder && (
        <div 
          data-testid="form-reminder-banner"
          className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-orange-500/10 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <ClipboardList className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-400" />
                  Complete Your Health Questionnaire
                </h3>
                <p className="text-gray-400 text-sm mt-0.5">
                  {pendingForms.length === 1 
                    ? `Please complete the "${pendingForms[0].name}" form to get started with your personalized fitness journey.`
                    : `You have ${pendingForms.length} required forms to complete before starting your training.`
                  }
                </p>
                <div className="flex items-center gap-2 mt-1 text-xs text-amber-400/80">
                  <Clock className="w-3 h-3" />
                  <span>Takes about 5-10 minutes</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                data-testid="complete-form-btn"
                onClick={() => navigate('/forms')}
                className="flex-1 sm:flex-none bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              >
                <ClipboardList className="w-4 h-4 mr-2" />
                Complete Now
              </Button>
              <button
                onClick={() => setDismissedReminder(true)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                title="Remind me later"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Daily Coaching Message Card */}
      {coachingMessage && (
        <div 
          data-testid="coaching-message-card"
          className="relative rounded-xl border border-primary/30 bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800"
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl pointer-events-none" />
          
          <div className="relative p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-lg"
                  style={{ backgroundColor: `${coachingMessage.category?.color}20` }}
                >
                  {coachingMessage.category?.icon || '💪'}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium text-primary uppercase tracking-wider">
                      Daily Coaching
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    Day {coachingMessage.day_number} of {coachingMessage.total_days}
                    {coachingMessage.cycle > 1 && ` • Cycle ${coachingMessage.cycle}`}
                  </span>
                </div>
              </div>
              
              {/* Streak Badge */}
              {coachingMessage.streak?.current > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <span className="text-sm font-semibold text-orange-400">
                    {coachingMessage.streak.current}
                  </span>
                </div>
              )}
            </div>
            
            {/* Message Content */}
            <div className="relative pl-4 border-l-2 border-primary/50 mb-4">
              <Quote className="absolute -left-3 -top-2 w-5 h-5 text-primary/30" />
              <p className="text-lg sm:text-xl text-white font-medium leading-relaxed">
                {coachingMessage.message}
              </p>
            </div>
            
            {/* Category Badge and Actions */}
            <div className="flex items-center justify-between">
              <div 
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
                style={{ 
                  backgroundColor: `${coachingMessage.category?.color}15`,
                  color: coachingMessage.category?.color 
                }}
              >
                <span>{coachingMessage.category?.icon}</span>
                <span>{coachingMessage.category?.name}</span>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Share Button */}
                <div className="relative">
                  <button
                    data-testid="share-message-btn"
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-zinc-800 hover:bg-zinc-700 text-gray-300 hover:text-white transition-all"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                  
                  {/* Share Menu Dropdown */}
                  {showShareMenu && (
                    <>
                      {/* Backdrop to close menu */}
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowShareMenu(false)}
                      />
                      <div 
                        data-testid="share-menu"
                        className="absolute right-0 top-full mt-2 w-52 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl z-50"
                      >
                        <div className="py-1">
                          <button
                            onClick={handleShareTwitter}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors text-left"
                          >
                            <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                            <span className="text-sm text-gray-300">Share on X</span>
                          </button>
                          <button
                            onClick={handleShareFacebook}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors text-left"
                          >
                            <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                            <span className="text-sm text-gray-300">Share on Facebook</span>
                          </button>
                          <button
                            onClick={handleShareWhatsApp}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors text-left"
                          >
                            <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                            <span className="text-sm text-gray-300">Share on WhatsApp</span>
                          </button>
                          <div className="border-t border-zinc-700 my-1" />
                          <button
                            onClick={handleCopyToClipboard}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors text-left"
                          >
                            {copiedToClipboard ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-400" />
                            )}
                            <span className="text-sm text-gray-300">
                              {copiedToClipboard ? 'Copied!' : 'Copy to Clipboard'}
                            </span>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Acknowledge Button */}
                <button
                  data-testid="acknowledge-message-btn"
                  onClick={handleAcknowledgeMessage}
                  disabled={messageAcknowledged}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    messageAcknowledged 
                      ? 'bg-green-500/20 text-green-400 cursor-default' 
                      : 'bg-primary/10 hover:bg-primary/20 text-primary hover:scale-105'
                  }`}
                >
                  <CheckCircle2 className={`w-4 h-4 ${messageAcknowledged ? 'fill-green-500' : ''}`} />
                  {messageAcknowledged ? 'Done!' : 'Got It'}
                </button>
              </div>
            </div>
            
            {/* Streak Bonus Message */}
            {coachingMessage.streak?.bonus_message && (
              <div className="mt-4 p-3 rounded-lg bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border border-orange-500/20">
                <p className="text-sm text-orange-300">
                  {coachingMessage.streak.bonus_message}
                </p>
              </div>
            )}
            
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>Journey Progress</span>
                <span>{Math.round((coachingMessage.day_number / 365) * 100)}%</span>
              </div>
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-primary to-red-400 rounded-full transition-all duration-500"
                  style={{ width: `${(coachingMessage.day_number / 365) * 100}%` }}
                />
              </div>
            </div>
            
            {/* Push Notification Toggle */}
            {pushSupported && (
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {pushEnabled ? (
                      <Bell className="w-4 h-4 text-green-500" />
                    ) : (
                      <BellOff className="w-4 h-4 text-gray-500" />
                    )}
                    <span className="text-sm text-gray-400">
                      {pushEnabled ? 'Daily reminders enabled' : 'Get daily coaching reminders'}
                    </span>
                  </div>
                  {pushEnabled ? (
                    <button
                      data-testid="test-notification-btn"
                      onClick={sendTestNotification}
                      className="text-xs px-3 py-1.5 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors"
                    >
                      Test
                    </button>
                  ) : (
                    <button
                      data-testid="enable-notifications-btn"
                      onClick={enableNotifications}
                      disabled={pushLoading}
                      className="text-xs px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
                    >
                      {pushLoading ? 'Enabling...' : 'Enable'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recovery Insights Widget */}
      <RecoveryInsightsWidget />

      {/* Announcements Feed */}
      {broadcasts.length > 0 && (
        <div className="card-tactical border-primary/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white uppercase flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-primary" />
              Latest Announcements
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/announcements')}
              className="text-gray-400 hover:text-white"
            >
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="space-y-3">
            {broadcasts.slice(0, 3).map((broadcast) => (
              <div
                key={broadcast.id}
                onClick={() => navigate('/announcements')}
                className="flex items-start gap-3 p-3 bg-secondary/30 rounded-sm border border-border hover:border-primary/50 cursor-pointer transition-colors"
              >
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0"
                  style={{ backgroundColor: `${broadcast.category_info?.color}20` }}
                >
                  {broadcast.category_info?.icon || '📢'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {broadcast.is_pinned && <Pin className="w-3 h-3 text-yellow-500" />}
                    <h4 className="text-white font-medium truncate">{broadcast.title}</h4>
                  </div>
                  <p className="text-sm text-gray-400 line-clamp-1">{broadcast.content}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Heart className={`w-3 h-3 ${broadcast.is_liked ? 'fill-red-500 text-red-500' : ''}`} />
                      {broadcast.likes_count}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="w-3 h-3" />
                      {broadcast.comments_count}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bento Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
        
        {/* Streak Card */}
        <div className="lg:col-span-3 card-tactical flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 uppercase text-sm tracking-wider">Current Streak</span>
            <Flame className="w-5 h-5 text-primary streak-fire" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-5xl font-bold text-white">{userStats.current_streak}</span>
            <span className="text-gray-400">days</span>
          </div>
          <div className="mt-4">
            <div className="text-sm text-gray-400">Keep it going!</div>
            <Progress value={(userStats.current_streak % 7) * 14.28} className="mt-2 h-2" />
          </div>
        </div>

        {/* Points Card */}
        <div className="lg:col-span-3 card-tactical flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 uppercase text-sm tracking-wider">Total Points</span>
            <Zap className="w-5 h-5 text-yellow-500" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-5xl font-bold text-white">{userStats.total_points.toLocaleString()}</span>
            <span className="text-gray-400">pts</span>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-primary" />
            <span className="text-sm text-gray-400">{userStats.badges_count} badges earned</span>
          </div>
        </div>

        {/* Steps Card */}
        <div className="lg:col-span-3 card-tactical">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 uppercase text-sm tracking-wider">Weekly Steps</span>
            <Footprints className="w-5 h-5 text-blue-500" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-4xl font-bold text-white">{stats.total_steps.toLocaleString()}</span>
          </div>
          <div className="mt-2 text-sm text-gray-400">
            <TrendingUp className="w-4 h-4 inline mr-1 text-green-500" />
            +12% from last week
          </div>
        </div>

        {/* Calories Card */}
        <div className="lg:col-span-3 card-tactical">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 uppercase text-sm tracking-wider">Calories Burned</span>
            <Flame className="w-5 h-5 text-orange-500" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-4xl font-bold text-white">{stats.total_calories.toLocaleString()}</span>
            <span className="text-gray-400">kcal</span>
          </div>
          <div className="mt-2 text-sm text-gray-400">
            This week
          </div>
        </div>

        {/* Activity Chart - Large */}
        <div className="lg:col-span-8 card-tactical">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading text-xl text-white uppercase">Weekly Activity</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary"></div>
                <span className="text-sm text-gray-400">Calories</span>
              </div>
            </div>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorCalories" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#E31B23" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#E31B23" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="day" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#121212',
                    border: '1px solid #27272A',
                    borderRadius: '4px',
                    color: '#fff'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="calories" 
                  stroke="#E31B23" 
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorCalories)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Habits Summary */}
        <div className="lg:col-span-4 card-tactical">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading text-xl text-white uppercase">Today's Habits</h3>
            <Target className="w-5 h-5 text-primary" />
          </div>
          
          <div className="text-center py-8">
            <div className="relative w-32 h-32 mx-auto">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="#27272A"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="#E31B23"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${habits.total > 0 ? (habits.completed_today / habits.total) * 352 : 0} 352`}
                  className="transition-all duration-500"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-mono text-3xl font-bold text-white">{habits.completed_today}</span>
                <span className="text-sm text-gray-400">of {habits.total}</span>
              </div>
            </div>
          </div>
          
          <Button 
            data-testid="view-habits-btn"
            onClick={() => navigate('/habits')}
            variant="outline"
            className="w-full border-border hover:border-primary hover:bg-primary/10"
          >
            View All Habits
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Quick Actions */}
        <div className="lg:col-span-6 card-tactical">
          <h3 className="font-heading text-xl text-white uppercase mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <button 
              data-testid="quick-action-ai-workout"
              onClick={() => navigate('/workouts')}
              className="p-4 bg-secondary/50 rounded-sm border border-border hover:border-primary/50 transition-colors text-left"
            >
              <Dumbbell className="w-6 h-6 text-primary mb-2" />
              <div className="text-white font-medium">AI Workout</div>
              <div className="text-sm text-gray-400">Generate a workout</div>
            </button>
            <button 
              data-testid="quick-action-meal-plan"
              onClick={() => navigate('/nutrition')}
              className="p-4 bg-secondary/50 rounded-sm border border-border hover:border-primary/50 transition-colors text-left"
            >
              <Activity className="w-6 h-6 text-green-500 mb-2" />
              <div className="text-white font-medium">Meal Plan</div>
              <div className="text-sm text-gray-400">Plan your meals</div>
            </button>
            <button 
              data-testid="quick-action-community"
              onClick={() => navigate('/community')}
              className="p-4 bg-secondary/50 rounded-sm border border-border hover:border-primary/50 transition-colors text-left"
            >
              <Heart className="w-6 h-6 text-pink-500 mb-2" />
              <div className="text-white font-medium">Community</div>
              <div className="text-sm text-gray-400">Connect with others</div>
            </button>
            <button 
              data-testid="quick-action-messages"
              onClick={() => navigate('/messages')}
              className="p-4 bg-secondary/50 rounded-sm border border-border hover:border-primary/50 transition-colors text-left"
            >
              <Calendar className="w-6 h-6 text-blue-500 mb-2" />
              <div className="text-white font-medium">Messages</div>
              <div className="text-sm text-gray-400">Chat with trainer</div>
            </button>
          </div>
        </div>

        {/* Upcoming Schedule */}
        <div className="lg:col-span-6 card-tactical">
          <h3 className="font-heading text-xl text-white uppercase mb-4">This Week</h3>
          <div className="space-y-3">
            {[
              { day: 'Today', workout: 'Upper Body Strength', time: '6:00 PM', active: true },
              { day: 'Tomorrow', workout: 'HIIT Cardio', time: '7:00 AM', active: false },
              { day: 'Wednesday', workout: 'Lower Body', time: '6:00 PM', active: false }
            ].map((item, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between p-4 rounded-sm border ${
                  item.active ? 'border-primary bg-primary/10' : 'border-border bg-secondary/30'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-2 h-10 rounded-full ${item.active ? 'bg-primary' : 'bg-gray-600'}`}></div>
                  <div>
                    <div className="text-white font-medium">{item.workout}</div>
                    <div className="text-sm text-gray-400">{item.day} • {item.time}</div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </div>
            ))}
          </div>
        </div>

        {/* Badges & Achievements */}
        <div className="lg:col-span-12 card-tactical">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl text-white uppercase">Your Badges</h3>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Award className="w-4 h-4 text-primary" />
              {badges.earned.length} / {badges.earned.length + badges.locked.length} Earned
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {badges.earned.map((badge) => (
              <div 
                key={badge.id}
                className="text-center p-4 bg-primary/10 border border-primary/30 rounded-sm"
                title={badge.description}
              >
                <div className="text-3xl mb-2">{badge.icon}</div>
                <div className="text-xs text-white font-medium truncate">{badge.name}</div>
                <div className="text-xs text-primary">+{badge.points} pts</div>
              </div>
            ))}
            {badges.locked.slice(0, 4).map((badge) => (
              <div 
                key={badge.id}
                className="text-center p-4 bg-secondary/30 border border-border rounded-sm opacity-50"
                title={badge.description}
              >
                <div className="text-3xl mb-2 grayscale">{badge.icon}</div>
                <div className="text-xs text-gray-400 font-medium truncate">{badge.name}</div>
                <div className="flex items-center justify-center gap-1 text-xs text-gray-500">
                  <Lock className="w-3 h-3" />
                  Locked
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
